// Roulette Jackpot Vault — locks 5× jackpot payouts for 24h.
// If claimed early: face value only. If unlocked (>24h): +50% bonus.
// VAULT FRENZY: on Sat/Sun (UTC), the unlock bonus is doubled to +100%.
// State is client-side only (mirrors chips/roulette).
import { addChips, getChips, spendChips } from "@/lib/chips";

const VAULT_KEY = "ppe_jackpot_vault_v1";
const LADDER_KEY = "ppe_vault_ladder_bonus_v1";      // permanent +5% base bonus
const RELOCKS_KEY = "ppe_vault_relocks_this_frenzy_v1"; // { weekendKey, count }
const LOCK_MS = 24 * 60 * 60 * 1000;
const BONUS_MULT_BASE = 1.5;
const BONUS_MULT_FRENZY = 2.0;
const LADDER_BONUS = 0.05; // +5% baked into base once ladder is unlocked

/** True once the player has re-locked the vault ≥ 2 times in a single Frenzy weekend. */
export function hasVaultLadderBonus() {
  try { return localStorage.getItem(LADDER_KEY) === "1"; }
  catch { return false; }
}

function frenzyWeekendKey(nowMs = Date.now()) {
  // Same key for consecutive Sat+Sun UTC. Uses the Saturday date as the identifier.
  const d = new Date(nowMs);
  const day = d.getUTCDay();
  if (day === 6) return d.toISOString().slice(0, 10);
  if (day === 0) {
    const sat = new Date(nowMs - 24 * 60 * 60 * 1000);
    return sat.toISOString().slice(0, 10);
  }
  return null;
}

/** True when today (UTC) is Saturday or Sunday. */
export function isVaultFrenzy(nowMs = Date.now()) {
  const d = new Date(nowMs);
  const day = d.getUTCDay(); // 0=Sun, 6=Sat
  return day === 0 || day === 6;
}

/** Current bonus multiplier — factors in Frenzy weekends + ladder-bonus perk. */
export function currentBonusMult(nowMs = Date.now()) {
  const base = isVaultFrenzy(nowMs) ? BONUS_MULT_FRENZY : BONUS_MULT_BASE;
  return hasVaultLadderBonus() ? base + LADDER_BONUS : base;
}

/** Ms until Frenzy ends (0 if not currently in Frenzy — then it's ms until next Frenzy starts). */
export function frenzyStatus(nowMs = Date.now()) {
  const d = new Date(nowMs);
  const day = d.getUTCDay();
  if (day === 0 || day === 6) {
    // In frenzy — ends at next Monday 00:00 UTC
    const end = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + (day === 0 ? 1 : 2), 0, 0, 0));
    return { active: true, msRemaining: end.getTime() - nowMs };
  }
  // Not in frenzy — next Saturday 00:00 UTC
  const daysToSat = (6 - day + 7) % 7;
  const start = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + daysToSat, 0, 0, 0));
  return { active: false, msRemaining: start.getTime() - nowMs };
}

// { chips, lockedAt } | null
function read() {
  try {
    const raw = localStorage.getItem(VAULT_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || typeof v.chips !== "number" || typeof v.lockedAt !== "number") return null;
    return v;
  } catch {
    return null;
  }
}
function write(v) {
  if (!v) localStorage.removeItem(VAULT_KEY);
  else localStorage.setItem(VAULT_KEY, JSON.stringify(v));
}

/** Returns the current vault or null if empty. */
export function getVault() {
  return read();
}

/** Deposit chips into the vault (called on 5× jackpot win). Replaces any existing. */
export function depositJackpot(chips) {
  if (!chips || chips <= 0) return null;
  const v = { chips: Math.floor(chips), lockedAt: Date.now() };
  write(v);
  return v;
}

/** Ms remaining until unlock. 0 or negative = unlocked. null if empty. */
export function msUntilUnlock() {
  const v = read();
  if (!v) return null;
  return v.lockedAt + LOCK_MS - Date.now();
}

/** True if vault has chips AND lock has expired. */
export function isVaultUnlocked() {
  const v = read();
  if (!v) return false;
  return Date.now() >= v.lockedAt + LOCK_MS;
}

/** Claim early — pays out face value with no bonus. Returns amount paid. */
export function claimEarly() {
  const v = read();
  if (!v) return 0;
  const paid = v.chips;
  addChips(paid);
  write(null);
  return paid;
}

/** Claim after unlock — pays out chips × current bonus mult (1.5× normal, 2× Frenzy). */
export function claimUnlocked() {
  const v = read();
  if (!v) return { paid: 0, bonus: 0, mult: 1 };
  if (Date.now() < v.lockedAt + LOCK_MS) return { paid: 0, bonus: 0, mult: 1 };
  const frenzy = isVaultFrenzy();
  const mult = currentBonusMult() + (v.extraBonus || 0);
  const total = Math.floor(v.chips * mult);
  const bonus = total - v.chips;
  addChips(total);
  write(null);
  // If this claim happened during Frenzy, permanently unlock the FRENZY SIREN skin.
  if (frenzy) {
    try { localStorage.setItem(FRENZY_SIREN_KEY, "1"); } catch {}
  }
  return { paid: total, bonus, mult };
}

/** Relock the vault for another 12h during Frenzy weekend for a chip cost.
 *  Adds an extra 25% bonus multiplier on top of the current mult.
 *  The 2nd re-lock in a single Frenzy weekend permanently unlocks the
 *  Vault Ladder Bonus (+5% base mult forever).
 *  Returns { ok, error?, newLockAt, appliedMult, ladderUnlocked } */
const FRENZY_SIREN_KEY = "ppe_frenzy_siren_v1";
export function relockVaultFrenzy(cost = 100, extraBonus = 0.25) {
  const v = read();
  if (!v) return { ok: false, error: "Vault empty" };
  if (!isVaultFrenzy()) return { ok: false, error: "Frenzy is not active" };
  if (!isVaultUnlocked()) return { ok: false, error: "Vault must be unlocked first" };
  if (getChips() < cost) return { ok: false, error: `Need ${cost} chips` };
  const spend = spendChips(cost);
  if (!spend.ok) return { ok: false, error: "Chip deduction failed" };
  // Re-lock for 12h and stack an extra bonus percentage
  const RELOCK_MS = 12 * 60 * 60 * 1000;
  v.lockedAt = Date.now() - (LOCK_MS - RELOCK_MS); // 12h remaining
  v.extraBonus = (v.extraBonus || 0) + extraBonus;
  write(v);
  // Ladder counter — 2 re-locks in the same weekend unlock the perk
  let ladderUnlocked = false;
  const wk = frenzyWeekendKey();
  if (wk && !hasVaultLadderBonus()) {
    let doc = { weekendKey: wk, count: 0 };
    try { doc = JSON.parse(localStorage.getItem(RELOCKS_KEY) || "null") || doc; } catch {}
    if (doc.weekendKey !== wk) doc = { weekendKey: wk, count: 0 };
    doc.count += 1;
    localStorage.setItem(RELOCKS_KEY, JSON.stringify(doc));
    if (doc.count >= 2) {
      try { localStorage.setItem(LADDER_KEY, "1"); } catch {}
      ladderUnlocked = true;
    }
  }
  return {
    ok: true,
    newLockAt: v.lockedAt,
    appliedMult: currentBonusMult() + v.extraBonus,
    ladderUnlocked,
  };
}

/** True once the player has claimed a vault during Frenzy weekend. */
export function hasFrenzySiren() {
  try { return localStorage.getItem(FRENZY_SIREN_KEY) === "1"; }
  catch { return false; }
}

/** Format ms into "Xh Ym" or "Ym Ss" for menu display. */
export function formatLockRemaining(ms) {
  if (ms == null) return "";
  if (ms <= 0) return "READY";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${s}s`;
}

export const JACKPOT_BONUS_MULT = BONUS_MULT_BASE;
export const JACKPOT_BONUS_MULT_FRENZY = BONUS_MULT_FRENZY;
